import logger from '@eventbrite/client-logger';
import { $FixMe } from '@eventbrite/ts-utils';
import { Dispatch } from 'redux';
import { Payment, PaymentMethod, RedirectInstructions } from '../models';

export enum ActionType {
    setIsAppRebrandActive = 'setIsAppRebrandActive',
    setLoading = 'setLoading',
    setRedirectInstructions = 'setRedirectInstructions',
    setPayment = 'setPayment',
}

export interface SetIsAppRebrandActiveAction {
    type: ActionType.setIsAppRebrandActive;
    payload: boolean;
}

export interface SetLoadingAction {
    type: ActionType.setLoading;
    payload: boolean;
}

export interface SetPaymentAction {
    type: ActionType.setPayment;
    payload: Payment;
}

export interface SetRedirectInstructionsAction {
    type: ActionType.setRedirectInstructions;
    payload: RedirectInstructions;
}

export type CheckoutBankRedirectAction =
    | SetIsAppRebrandActiveAction
    | SetLoadingAction
    | SetPaymentAction
    | SetRedirectInstructionsAction;

export const setIsAppRebrandActive = (
    isAppRebrandActive: boolean,
): SetIsAppRebrandActiveAction => ({
    type: ActionType.setIsAppRebrandActive,
    payload: isAppRebrandActive,
});

export const setPayment = ({
    paymentMethod,
    bankName,
    stripePaymentType,
}: {
    paymentMethod: PaymentMethod;
    bankName: string;
    stripePaymentType: string | null;
}): SetPaymentAction => ({
    type: ActionType.setPayment,
    payload: { paymentMethod, bankName, stripePaymentType },
});

export const setRedirectInstructions = (
    redirectInstructions: RedirectInstructions,
): SetRedirectInstructionsAction => ({
    type: ActionType.setRedirectInstructions,
    payload: redirectInstructions,
});

interface AppData {
    _getRedirectedPaymentOrderPromise: () => Promise<RedirectInstructions>;
    bankName: string;
    isAppRebrandActive: boolean;
    paymentMethod: PaymentMethod;
    redirectInstructions?: RedirectInstructions;
    stripePaymentType: string | null;
}

const getAppData = (): AppData | null => {
    const windowAppData: string | null =
        window.__SERVER_DATA__.request.params.appData;
    const parsedWindowAppData: AppData | null = windowAppData
        ? JSON.parse(windowAppData)
        : null;

    /**
     * EB-95856 Some users cannot progress to order form on Embedded Checkout on some iOS devices
     * because web views are removing `window.opener` data, so we pass that data through GET parameters.
     *
     * @see `packages/attendee/checkout/src/actions/order/utils.js::redirectToExternalPayment`
     */

    return parsedWindowAppData ?? (window as $FixMe).opener?.app;
};

const _getRedirectedPaymentOrderPromise = async <State>(
    dispatch: Dispatch<State>,
) => {
    try {
        const appData = getAppData();

        if (!appData) {
            window.close();
            return;
        }

        dispatch(setPayment(appData));
        dispatch(setIsAppRebrandActive(appData.isAppRebrandActive));

        const redirectInstructions =
            appData.redirectInstructions ||
            (await appData._getRedirectedPaymentOrderPromise());

        dispatch(setRedirectInstructions(redirectInstructions));
    } catch (error) {
        const err = new CustomEvent('redirectError', {
            detail: error,
        });

        logger.error('checkoutWidgetBankRedirect unhandled error', error);
        window.opener?.dispatchEvent(err);
        window.close();
    }
};

export const setLoading =
    () =>
    <State>(dispatch: Dispatch<State>) => {
        _getRedirectedPaymentOrderPromise(dispatch);

        const setLoadingAction: SetLoadingAction = {
            type: ActionType.setLoading,
            payload: true,
        };

        return dispatch(setLoadingAction);
    };
